import dayjs, { type Dayjs } from "dayjs";
import type { Granularity } from "@lib/api-hooks/useKpiBreakdownForChannel";

type RangeOptions = {
  date: string;
  granularity?: Granularity;
  isFirstTick?: boolean;
  isLastTick?: boolean;
  startDate?: string | null;
  endDate?: string | null;
};

type AxisLabelOptions = {
  range: { start: Dayjs; end: Dayjs };
  granularity?: Granularity;
  showYear?: boolean;
  isFirstTick?: boolean;
  isLastTick?: boolean;
};

type TooltipLabelOptions = RangeOptions & {
  date: string;
};

export function getGranularityBucketRange({
  date,
  granularity = "daily",
  isFirstTick,
  isLastTick,
  startDate,
  endDate,
}: RangeOptions): { start: Dayjs; end: Dayjs } {
  const bucketStart = dayjs(date);
  let bucketEnd = bucketStart;

  switch (granularity) {
    case "weekly":
      bucketEnd = bucketStart.add(6, "day");
      break;
    case "monthly":
      bucketEnd = bucketStart.endOf("month");
      break;
    default:
      break;
  }

  let start = bucketStart;
  let end = bucketEnd;

  if (isFirstTick && startDate) {
    const explicitStart = dayjs(startDate);
    if (explicitStart.isValid()) {
      start = explicitStart;
    }
  }

  if (end.isBefore(start)) {
    end = start;
  }

  return { start, end };
}

/**
 * Produces axis tick label lines that reflect the current granularity.
 * @return {string[]} An array of one or two strings that should be rendered on separate lines.
 */
export function formatGranularityAxisLabel({
  range,
  granularity = "daily",
  showYear = false,
  isFirstTick,
  isLastTick,
}: AxisLabelOptions): string[] {
  const { start, end } = range;

  if (!start.isValid()) {
    return ["-"];
  }

  switch (granularity) {
    case "weekly": {
      const primary = start.format("DD MMM");
      const yearLine =
        showYear || isFirstTick || isLastTick
          ? start.format("YYYY")
          : undefined;
      return yearLine ? [primary, yearLine] : [primary];
    }
    case "monthly": {
      if (isFirstTick) {
        const firstLabel = start.format("DD MMM");
        return [firstLabel, start.format("YYYY")];
      }

      const monthLabel = start.format("MMM");
      const yearLine = showYear ? start.format("YYYY") : undefined;
      return yearLine ? [monthLabel, yearLine] : [monthLabel];
    }
    case "none":
    case "daily":
    default: {
      const primary = start.format("DD MMM");
      const yearLine =
        showYear || isFirstTick || isLastTick
          ? start.format("YYYY")
          : undefined;
      return yearLine ? [primary, yearLine] : [primary];
    }
  }
}

/**
 * Formats tooltip labels so users see the exact range each bucket covers.
 * @return {string} The formatted tooltip label.
 */
export function formatGranularityTooltipLabel({
  date,
  granularity = "daily",
  isFirstTick,
  isLastTick,
  startDate,
  endDate,
}: TooltipLabelOptions): string {
  const { start, end: computedEnd } = getGranularityBucketRange({
    date,
    granularity,
    isFirstTick,
    isLastTick,
    startDate,
    endDate,
  });

  if (!start.isValid()) {
    return "";
  }

  let end = computedEnd;
  if (isLastTick && endDate) {
    const explicitEnd = dayjs(endDate);
    if (explicitEnd.isValid() && explicitEnd.isBefore(end)) {
      end = explicitEnd;
    }
  }

  if (granularity === "daily" || start.isSame(end, "day")) {
    return start.format("DD MMM YYYY");
  }

  return `${start.format("DD MMM YYYY")} – ${end.format("DD MMM YYYY")}`;
}
